import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import EditIcon from 'transfix-icons/build/EditIcon'
import { Card } from 'transfix-ui/components/Card'
import { IconButton } from 'transfix-ui/components/IconButton'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Box } from 'transfix-ui/core'
import { ICarrierState, ICarrierContactState } from 'types/carrier'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import {
  openContactInformationDialog,
  getContactInfo,
} from 'apps/carrier/utils/actions/carrierContact'
import { IStore } from 'apps/carrier/utils/reducers'
import { ConnectedPhoneNumber } from 'apps/marketplace/components/ConnectedPhoneNumber'
import { lookupCountryByCountryCode } from 'utils/getLocations'
import { DISPLAY } from 'constants/display'
import ContactDialog from './ContactDialog'

interface ISelector {
  carrier: ICarrierState
  carrierContact: ICarrierContactState
}

export const Contact = () => {
  const isHighwayMilestoneFourComplianceTabEnabled = useFeatureFlag(
    'highwayMilestoneFourComplianceTab'
  )
  const {
    carrier: { carrierDetails },
    carrierContact: { contact, error, loading, showDialog: showEditDialog },
  } = useSelector<IStore, ISelector>(({ carrier, carrierContact }) => ({
    carrier,
    carrierContact,
  }))
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(getContactInfo(carrierDetails?.id!))
  }, [dispatch, carrierDetails?.id])
  const showDialog = () => {
    dispatch(openContactInformationDialog())
  }
  const {
    contactEmail = '',
    location: {
      address,
      addressLine2,
      city,
      contactPhone,
      contactPhoneExt,
      countryCode,
      stateCode,
      zipcode,
    } = {
      contactPhone: '',
      contactPhoneExt: '',
      address: '',
      addressLine2: '',
      countryCode: '',
      city: '',
      stateCode: '',
      zipcode: '',
    },
  } = contact
  return (
    <Card>
      <Box className='row no-gutter pv4'>
        <h3 className='fl mb0 mt2'>
          {isHighwayMilestoneFourComplianceTabEnabled
            ? 'Accounting Information'
            : 'Contact Information'}
        </h3>
        <Box className='fr'>
          <IconButton
            Icon={EditIcon}
            color='tertiary'
            onClick={showDialog}
            data-test='edit-contact'
            label='Edit Contact'
            size='large'
          />
        </Box>
      </Box>
      {loading && <Spinner position='relative' />}
      {error && <InlineMessage message={error} type={INLINE_MESSAGE_VARIANTS.ALERT} />}
      {!loading && !error && (
        <>
          <Box className='row no-gutter'>
            <Box className='col-xs-4'>
              <small>Carrier Contact Email</small>
              <Box>
                {contactEmail ? (
                  <a className='break-word' href={`mailto:${contactEmail}`}>
                    {contactEmail}
                  </a>
                ) : (
                  DISPLAY.empty
                )}
              </Box>
            </Box>
            <Box className='col-xs-4'>
              <small>Phone</small>
              {contactPhone && (
                <Box>
                  <ConnectedPhoneNumber phoneNumber={contactPhone} />
                  {contactPhoneExt && <span> ext {contactPhoneExt}</span>}
                </Box>
              )}
              {!contactPhone && <Box>{DISPLAY.empty}</Box>}
            </Box>
          </Box>
          <Box className='row no-gutter'>
            <Box className='col-xs-4'>
              <small>Billing Address</small>
              <Box>
                {address || DISPLAY.empty}
                {addressLine2 ? `, ${addressLine2}` : ''}
              </Box>
            </Box>
            <Box className='col-xs-4'>
              <small>Country</small>
              <Box>
                {countryCode ? `${lookupCountryByCountryCode(countryCode)}` : DISPLAY.empty}
              </Box>
            </Box>
          </Box>
          <Box className='row no-gutter'>
            <Box className='col-xs-4'>
              <small>City</small>
              <Box>{city ? `${city}, ${stateCode}` : DISPLAY.empty}</Box>
            </Box>
            <Box className='col-xs-4'>
              <small>Postal Code</small>
              <Box>{zipcode || DISPLAY.empty}</Box>
            </Box>
          </Box>
        </>
      )}
      {showEditDialog && <ContactDialog />}
    </Card>
  )
}

export default Contact
